import { ChevronRight } from 'lucide-react';

import type {
  CandidateCardResult,
  ChatResult,
  ChoiceResult,
  ConfirmationOutcome,
} from '@/modules/hiring-manager/chat/chatResults';

import { CandidateList } from './CandidateList';
import { type ChatDecision, ConfirmationCard } from './ConfirmationCard';
import { SendButton } from './SendButton';

/** A candidate to open beside the chat. */
export interface ChatCandidate {
  jobId: string;
  candidateId: string;
  /** Their page in the web app, for when the side panel is taken. */
  url: string | null;
}

/** What a reply's results need from the chat: how to send, decide and open, and what's running. */
export interface ReplyResultsHandlers {
  /** What became of each confirmation, by its id (`confirmationOutcomes`). */
  outcomes: Map<string, ConfirmationOutcome>;
  /** Sends a button's message as the HM's next message. */
  onSend: (text: string) => void;
  /** Runs Confirm or Cancel under a change Finlay proposed. */
  onDecide: (confirmationId: string, decision: ChatDecision) => void;
  /** Opens a candidate beside the chat. */
  onOpenCandidate: (candidate: ChatCandidate) => void;
  /** A turn or a click is running: the buttons wait. */
  busy: boolean;
  /** The message being sent, so the button that sent it shows it's working. */
  sending?: string;
  /** The click being run, so its button shows it's working. */
  deciding?: { confirmationId: string; decision: ChatDecision };
}

interface ReplyResultsProps extends ReplyResultsHandlers {
  results: ChatResult[];
}

/**
 * The results Finlay drew under one reply: changes to confirm, choices, candidate lists, and a line for a candidate
 * whose card opens beside the chat. The job intake's results are the side panel's, and outcomes show on the change
 * they answer.
 */
export function ReplyResults({ results, ...handlers }: ReplyResultsProps) {
  const drawn = results.flatMap((result, index) => {
    const key = `${result.type}-${index}`;
    switch (result.type) {
      case 'confirmation':
        return [
          <ConfirmationCard
            confirmation={result}
            deciding={
              handlers.deciding?.confirmationId === result.confirmationId
                ? handlers.deciding.decision
                : undefined
            }
            disabled={handlers.busy}
            key={key}
            onDecide={(decision) =>
              handlers.onDecide(result.confirmationId, decision)
            }
            outcome={handlers.outcomes.get(result.confirmationId)}
          />,
        ];
      case 'choice':
        return [<Choice choice={result} handlers={handlers} key={key} />];
      case 'candidate_list':
        return [
          <CandidateList
            disabled={handlers.busy}
            key={key}
            list={result}
            onOpen={(row) =>
              row.candidateId
                ? handlers.onOpenCandidate({
                    jobId: row.job.id,
                    candidateId: row.candidateId,
                    url: row.url,
                  })
                : handlers.onSend(row.details.message)
            }
            onSend={handlers.onSend}
            sending={handlers.sending}
          />,
        ];
      case 'candidate_card':
        return [<CandidateLine card={result} handlers={handlers} key={key} />];
      default:
        return [];
    }
  });

  return drawn.length > 0 ? (
    <div className="flex flex-col gap-3">{drawn}</div>
  ) : null;
}

// The reply asks the question; the options are the answers to click.
function Choice({
  choice,
  handlers,
}: {
  choice: ChoiceResult;
  handlers: ReplyResultsHandlers;
}) {
  return (
    <div
      aria-label={choice.question}
      className="flex flex-wrap gap-2"
      role="group"
    >
      {choice.options.map((option) => (
        <SendButton
          action={option}
          disabled={handlers.busy}
          key={option.ref}
          onSend={handlers.onSend}
          sending={handlers.sending}
        />
      ))}
    </div>
  );
}

// The candidate's card opens beside the chat; the line under the reply keeps their decisions at hand.
function CandidateLine({
  card,
  handlers,
}: {
  card: CandidateCardResult;
  handlers: ReplyResultsHandlers;
}) {
  return (
    <section
      aria-label={card.name}
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border px-4 py-2.5 text-[13px]"
    >
      <button
        className="group flex min-w-0 flex-1 items-center gap-2 text-left"
        onClick={() =>
          handlers.onOpenCandidate({
            jobId: card.job.id,
            candidateId: card.candidateId,
            url: card.url,
          })
        }
        type="button"
      >
        <span className="min-w-0 flex-1 truncate">
          <span className="font-medium group-hover:underline">{card.name}</span>
          <span className="text-muted-foreground">
            {' '}
            · {card.job.title} · {card.stageLabel}
          </span>
        </span>
        <ChevronRight className="text-muted-foreground size-3.5 shrink-0" />
      </button>
      {card.actions.length > 0 && (
        <div className="flex gap-2">
          {card.actions.map((action) => (
            <SendButton
              action={action}
              disabled={handlers.busy}
              key={action.label}
              onSend={handlers.onSend}
              sending={handlers.sending}
            />
          ))}
        </div>
      )}
    </section>
  );
}
